import React from 'react'
import { FlashIcon, CoinIcon, type InventoryItemData } from '@repo/ui'
import { toInteger } from '@repo/money'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { displayAmountClean } from 'src/utils/displayAmountClean'
import { isBooster, isBonus, type InventoryItem, type Booster, type Bonus } from '../types/domain'
import {
  createBoosterButton,
  createAutoBoosterButton,
  createBonusButton,
  createUnclaimButton
} from '../factories/ctaButtons.factories'

interface MapperContext {
  t: (key: string, params?: any) => string
  applicableGamesText: string
  isApplicableToGame: boolean
  minBetAmount?: number
  onActivateItem: (
    e: React.MouseEvent | React.PointerEvent,
    itemId: string,
    isApplicableToGame: boolean
  ) => void
  onActivateBonus: (
    e: React.MouseEvent | React.PointerEvent,
    itemId: string,
    amount: number,
    isButtonDisabled: boolean
  ) => void
  onDeactivateItem: (e: React.MouseEvent | React.PointerEvent, itemId: string) => void
}

const createDescription = (baseDescription: string, applicableGamesText: string): string => {
  return applicableGamesText ? `${baseDescription}\n${applicableGamesText}` : baseDescription
}

export const mapBoosterToItemData = (item: Booster, context: MapperContext): InventoryItemData => {
  const { t, applicableGamesText, isApplicableToGame, onActivateItem } = context

  const boostValue =
    item.boost.unit === 'percentage' ? `+${item.boost.value}%` : `+${item.boost.value}`

  const baseDescription = t('Activate this to get a {{value}} multiplier boost on your next bet', {
    value: boostValue
  })
  const description = createDescription(baseDescription, applicableGamesText)

  const footer =
    item.activationType === 'manual'
      ? createBoosterButton(t, isApplicableToGame, (e) => {
          onActivateItem(e, item.id, isApplicableToGame)
        })
      : createAutoBoosterButton(t)

  return {
    id: item.id,
    name: t('Booster'),
    quantity: boostValue,
    icon: <FlashIcon className="w-[14px] h-[14px] drop-shadow-[0_0_2px_rgba(0,0,0,0.25)]" />,
    accentColor: 'blue' as const,
    description,
    subtitle: t('Booster'),
    footer
  }
}

export const mapBonusToItemData = (
  item: Bonus,
  context: MapperContext,
  isAlreadyActivated: boolean,
  hasAnyBonusActivated: boolean
): InventoryItemData => {
  const {
    t,
    applicableGamesText,
    isApplicableToGame,
    minBetAmount,
    onActivateBonus,
    onDeactivateItem
  } = context

  const shouldShowButton = !isAlreadyActivated && !hasAnyBonusActivated
  const isBelowMinBet =
    minBetAmount !== undefined && toInteger(item.amount, item.currency) < minBetAmount
  const isButtonDisabled = !isApplicableToGame || isBelowMinBet

  const baseDescription = t('Activate this bonus to get a free bet of {{value}}', {
    value: `${getCurrencySymbol(item.currency)}${displayAmountClean(item.amount)}`
  })
  const description = createDescription(baseDescription, applicableGamesText)

  let footer: React.ReactNode = undefined

  if (shouldShowButton) {
    const disabledReason = isBelowMinBet ? 'minBet' : !isApplicableToGame ? 'game' : undefined
    footer = createBonusButton(
      t,
      isButtonDisabled,
      (e) => {
        onActivateBonus(e, item.id, item.amount, isButtonDisabled)
      },
      disabledReason
    )
  } else if (isAlreadyActivated) {
    footer = createUnclaimButton(t, (e) => {
      onDeactivateItem(e, item.id)
    })
  }

  return {
    id: item.id,
    name: t('Free bet'),
    quantity: `${getCurrencySymbol(item.currency)}${displayAmountClean(item.amount)}`,
    icon: <CoinIcon className="w-[14px] h-[14px] drop-shadow-[0_0_2px_rgba(0,0,0,0.25)]" />,
    accentColor: 'green' as const,
    description,
    subtitle: t('Free bet'),
    footer
  }
}

export const mapInventoryItemToItemData = (
  item: InventoryItem,
  context: MapperContext,
  activatedItemIds: string[],
  inventoryItems: InventoryItem[]
): InventoryItemData | null => {
  if (isBooster(item)) {
    return mapBoosterToItemData(item, context)
  }

  if (isBonus(item)) {
    const isAlreadyActivated = activatedItemIds.includes(item.id)
    const hasAnyBonusActivated = inventoryItems.some(
      (i) => isBonus(i) && activatedItemIds.includes(i.id)
    )

    return mapBonusToItemData(item, context, isAlreadyActivated, hasAnyBonusActivated)
  }

  // Fallback for unknown item types
  return null
}
